import {
    boot,
    Intercom,
    onShow,
    shutdown,
    update,
} from '@intercom/messenger-js-sdk';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';

import { subscribeToDockedFooterHeight } from '@apps/web/hooks/use_composer_footer_height';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { EVENTS } from '@apps/web/lib/events';
import { isExtensionContext } from '@apps/web/lib/extension_context';
import { posthog } from '@apps/web/lib/posthog';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { useUser } from '@apps/web/swr_data_fetching/current_user_queries';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

// The launcher's resting distance from the bottom of the screen. The Phoebe
// chat launcher matches it (see LEGACY_LAUNCHER_DEFAULT_STYLE in
// components/layout/app_layout.tsx) so the two bubbles sit at the same height.
const INTERCOM_LAUNCHER_VERTICAL_PADDING_PX = 40;

type IntercomProviderProps = {
    readonly children: ReactNode;
};

export function IntercomProvider({ children }: IntercomProviderProps) {
    const { data: user } = useUser({ reason: 'intercom-provider' });
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'intercom-provider',
    });
    const hasBooted = useRef(false);
    const previousUserId = useRef<string | null>(null);
    // Bars docked to the bottom of the screen — a message composer, the shifts
    // calendar's outreach selection bar — publish their height here. Intercom
    // positions its launcher from a config number rather than CSS, so it
    // cannot read the `--composer-footer-height` variable the Phoebe launcher
    // uses and has to be told instead; without this the bubble covers whatever
    // is docked underneath it.
    //
    // Applied on phone layouts only. A docked bar spans the full width there,
    // so the launcher is always on top of it; on a wider screen the launcher
    // has its own corner and today's resting position is deliberate. Whether
    // the desktop launcher should also clear a composer is a separate call.
    const isMobile = useIsMobile();
    const [dockedFooterHeightPx, setDockedFooterHeightPx] = useState(0);
    useEffect(() => subscribeToDockedFooterHeight(setDockedFooterHeightPx), []);
    const launcherVerticalPaddingPx =
        INTERCOM_LAUNCHER_VERTICAL_PADDING_PX +
        (isMobile ? dockedFooterHeightPx : 0);

    useEffect(() => {
        const { intercomAppId } = getRuntimeConfig();
        if (!intercomAppId || isExtensionContext()) {
            if (hasBooted.current) {
                shutdown();
                hasBooted.current = false;
            }
            return;
        }

        // Shutdown and reboot if user changed (logout or different user login)
        if (
            hasBooted.current &&
            previousUserId.current !== null &&
            previousUserId.current !== (user?.id ?? null)
        ) {
            shutdown();
            hasBooted.current = false;
        }

        const intercomConfig = user?.email
            ? {
                  app_id: intercomAppId,
                  vertical_padding: launcherVerticalPaddingPx,
                  user_id: user.id,
                  name:
                      [user.first_name, user.last_name]
                          .filter(Boolean)
                          .join(' ') || user.email,
                  email: user.email,
                  ...(organization && {
                      company: {
                          company_id: organization.id,
                          name: organization.internal_name
                              ? `${organization.name} (${organization.internal_name})`
                              : organization.name,
                      },
                  }),
              }
            : {
                  app_id: intercomAppId,
                  vertical_padding: launcherVerticalPaddingPx,
              };

        if (!hasBooted.current) {
            // Once the SDK snippet has installed `window.Intercom` (it
            // survives provider unmounts, e.g. sign-out remounting the
            // authenticated app), a repeat `Intercom()` call only re-attaches
            // and updates — it never re-boots after a shutdown. Use the
            // page-global the SDK itself branches on to pick the entry point.
            if (typeof window.Intercom === 'function') {
                boot(intercomConfig);
            } else {
                Intercom(intercomConfig);
            }
            hasBooted.current = true;
        } else {
            // Update existing session with new data
            update(intercomConfig);
        }

        previousUserId.current = user?.id ?? null;
    }, [user, organizationId, organization, launcherVerticalPaddingPx]);

    useEffect(() => {
        if (!hasBooted.current || !user?.email || !organization) {
            return;
        }

        // Intercom has no unsubscribe API, so ignore callbacks from an old
        // user or organization after the provider changes or unmounts.
        let active = true;
        onShow(() => {
            if (active) {
                posthog.capture(EVENTS.INTERCOM_MESSENGER_OPENED, {
                    user_id: user.id,
                    user_email: user.email,
                    organization_id: organization.id,
                    organization_name: organization.name,
                });
            }
        });
        return () => {
            active = false;
        };
    }, [user, organization]);

    // Shutdown Intercom on unmount
    useEffect(() => {
        return () => {
            if (hasBooted.current) {
                shutdown();
                hasBooted.current = false;
            }
        };
    }, []);

    return <React.Fragment>{children}</React.Fragment>;
}
